// src/components/TokenAirdrop.js
import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, 
  Upload, 
  Plus, 
  Trash2, 
  Send, 
  CheckCircle, 
  AlertCircle,
  Download,
  FileText,
  RefreshCw
} from 'lucide-react';
import './TokenAirdrop.css';

// XLSX 라이브러리 동적 import를 위한 설정
let XLSX = null;

// XLSX 라이브러리 로드
const loadXLSX = async () => {
  if (!XLSX) {
    try {
      XLSX = await import('xlsx');
    } catch (error) {
      console.error('XLSX 라이브러리 로드 실패:', error);
      alert('Excel 파일 처리를 위해 필요한 라이브러리를 로드할 수 없습니다.');
      return null;
    }
  }
  return XLSX;
};

// WorldLand Explorer API 클래스
class WorldLandExplorerAPI {
  constructor() {
    this.baseUrl = 'https://scan.worldland.foundation';
  }

  // 계정의 모든 토큰 잔액 조회
  async getAccountTokenBalances(address) {
    try {
      const endpoint = `${this.baseUrl}/api/v2/addresses/${address}/token-balances`;
      
      console.log('토큰 잔액 조회 API 요청:', endpoint);

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(15000)
      });

      if (!response.ok) {
        throw new Error(`토큰 잔액 API 요청 실패: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // 잔액이 0이 아닌 토큰만 필터링
      const nonZeroTokens = (data || []).filter(token => {
        const balance = token.value || token.balance || '0';
        return balance !== '0' && parseFloat(balance) > 0;
      });
      
      return {
        success: true,
        tokens: this.formatTokenBalances(nonZeroTokens),
        source: 'blockscout-balances'
      };

    } catch (error) {
      console.warn('토큰 잔액 조회 실패:', error.message);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 인기 토큰 목록 조회
  async getPopularTokens(limit = 20) {
    try {
      const endpoint = `${this.baseUrl}/api/v2/tokens`;
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        sort: 'holder_count',
        order: 'desc'
      });

      const url = `${endpoint}?${params}`;
      console.log('인기 토큰 API 요청:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        signal: AbortSignal.timeout(10000)
      });

      if (!response.ok) {
        throw new Error(`인기 토큰 API 요청 실패: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: true,
        tokens: this.formatTokenList(data.items || []),
        source: 'popular-tokens'
      };

    } catch (error) {
      console.warn('인기 토큰 조회 실패:', error.message);
      return this.getDefaultTokenList();
    }
  }

  // 기본 토큰 목록 (API 실패 시 사용)
  getDefaultTokenList() {
    const defaultTokens = [
      {
        address: '0xc93f06d582fd6fD7FfEd928ffB57896474abC855',
        name: 'SAPSALDOG',
        symbol: 'Dokdo',
        decimals: 18,
        verified: true
      },
      {
        address: '0xDBEDbeB2CD8493AE2d746b7eeD597946F6E7910A',
        name: 'WorldLand Cafe',
        symbol: 'CAFE',
        decimals: 18,
        verified: true
      },
      {
        address: '0x6f57a06f1eA3c6EB16F742249CBdCB672458337C',
        name: 'WorldLand-Pay',
        symbol: 'WLP',
        decimals: 18,
        verified: true
      },
      {
        address: '0xC0AD918E5CC5930B8D31cc229770f232Dd82187a',
        name: 'Doldari',
        symbol: 'DOL',
        decimals: 18,
        verified: true
      },
      {
        address: '0x12183c232B94a6FB80711037b8A407a03Dd24983',
        name: 'World Vision',
        symbol: 'VISION',
        decimals: 18,
        verified: true
      }
    ];

    return {
      success: true,
      tokens: defaultTokens.map(token => ({
        ...token,
        balance: '0',
        balanceRaw: '0'
      })),
      source: 'default-tokens'
    };
  }

  // 토큰 목록 데이터 포맷팅
  formatTokenList(tokens) {
    return tokens.map(token => ({
      address: token.address,
      name: token.name || 'Unknown Token',
      symbol: token.symbol || 'UNK',
      decimals: parseInt(token.decimals || '18'),
      type: token.type || 'ERC-20',
      totalSupply: token.total_supply || '0',
      holderCount: token.holders_count || 0,
      verified: token.is_verified || false,
      balance: '0',
      balanceRaw: '0'
    }));
  }

  // 토큰 잔액 데이터 포맷팅
  formatTokenBalances(tokens) {
    return tokens.map(token => ({
      address: token.token?.address || token.address,
      name: token.token?.name || token.name || 'Unknown Token',
      symbol: token.token?.symbol || token.symbol || 'UNK',
      decimals: parseInt(token.token?.decimals || token.decimals || '18'),
      balance: this.formatTokenAmount(
        token.value || token.balance || '0', 
        parseInt(token.token?.decimals || token.decimals || '18')
      ),
      balanceRaw: token.value || token.balance || '0',
      type: token.token?.type || 'ERC-20',
      verified: token.token?.is_verified || false
    }));
  }

  // 토큰 수량 포맷팅
  formatTokenAmount(amount, decimals) {
    try {
      if (!amount || amount === '0') return '0';
      const divisor = Math.pow(10, decimals);
      const formatted = (parseInt(amount) / divisor).toString();
      return parseFloat(formatted).toFixed(6);
    } catch {
      return '0';
    }
  }

  // WLC 잔액 조회
  async getWLCBalance(address) {
    try {
      const provider = window.selectedEthereumProvider || window.ethereum;
      const balance = await provider.request({
        method: 'eth_getBalance',
        params: [address, 'latest'],
      });
      const balanceInEther = parseInt(balance, 16) / Math.pow(10, 18);
      return balanceInEther;
    } catch (error) {
      console.error('WLC 잔액 조회 실패:', error);
      return 0;
    }
  }

  // 특정 토큰 잔액 조회
  async getTokenBalance(address, tokenAddress, decimals = 18) {
    try {
      const provider = window.selectedEthereumProvider || window.ethereum;
      
      // ERC-20 balanceOf function signature
      const balanceOfFunction = '0x70a08231';
      const paddedAddress = address.substring(2).padStart(64, '0');
      const data = balanceOfFunction + paddedAddress;

      const result = await provider.request({
        method: 'eth_call',
        params: [{
          to: tokenAddress,
          data: data
        }, 'latest']
      });

      const balance = parseInt(result, 16) / Math.pow(10, decimals);
      return balance;
    } catch (error) {
      console.error('토큰 잔액 조회 실패:', error);
      return 0;
    }
  }
}

const TokenAirdrop = ({ account, onBack, user, userProfile }) => {
  const [recipients, setRecipients] = useState([]);
  const [tokenAddress, setTokenAddress] = useState('');
  const [isWLC, setIsWLC] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [errors, setErrors] = useState([]);
  const [successCount, setSuccessCount] = useState(0);
  const [userTokens, setUserTokens] = useState([]);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [explorerAPI] = useState(new WorldLandExplorerAPI());
  const [airdropRights, setAirdropRights] = useState({ hasRights: false, numTokens: 0 });
  const [isCheckingRights, setIsCheckingRights] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const fileInputRef = useRef(null);

  // 지갑 연결 상태 확인
  useEffect(() => {
    if (account) {
      setIsConnected(true);
    } else {
      setIsConnected(false);
    }
  }, [account]);

  // 지갑 연결 해제 함수
  const disconnectWallet = () => {
    try {
      console.log('🔓 지갑 연결 해제 시작...');
      
      // 1. 선택된 이더리움 프로바이더 초기화
      if (window.selectedEthereumProvider) {
        delete window.selectedEthereumProvider;
        console.log('✅ selectedEthereumProvider 삭제됨');
      }

      // 2. 로컬 스토리지에서 지갑 관련 데이터 삭제
      const walletKeys = [
        'walletconnect',
        'WALLETCONNECT_DEEPLINK_CHOICE',
        'metamask-provider',
        'phantom-provider',
        'ethereum-provider',
        'injected-provider',
        'wallet-type',
        'connected-wallet',
        'last-connected-wallet',
        'wallet-connection',
        'web3-wallet-type',
        'ethereum.enable',
        'selectedAddress',
        'accounts',
        'chainId',
        'networkId'
      ];

      walletKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (error) {
          console.warn(`⚠️ ${key} 삭제 실패:`, error);
        }
      });

      // 3. WalletConnect 관련 데이터 삭제 (키 패턴으로 찾기)
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && (
            key.includes('walletconnect') ||
            key.includes('wc@2') ||
            key.includes('@walletconnect') ||
            key.includes('wallet-connect') ||
            key.includes('metamask') ||
            key.includes('phantom')
          )) {
            localStorage.removeItem(key);
            console.log(`🗑️ 삭제된 키: ${key}`);
          }
        }

        // 세션 스토리지도 동일하게 정리
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key && (
            key.includes('walletconnect') ||
            key.includes('wc@2') ||
            key.includes('@walletconnect') ||
            key.includes('wallet-connect') ||
            key.includes('metamask') ||
            key.includes('phantom')
          )) {
            sessionStorage.removeItem(key);
            console.log(`🗑️ 세션에서 삭제된 키: ${key}`);
          }
        }
      } catch (error) {
        console.warn('⚠️ 패턴 기반 키 삭제 실패:', error);
      }

      // 4. 컴포넌트 상태 초기화
      setIsConnected(false);
      setUserTokens([]);
      setSelectedToken(null);
      setRecipients([]);
      setIsWLC(true);
      setTokenAddress('');
      setErrors([]);
      
      console.log('✅ 지갑 연결 해제 완료');
      alert('지갑 연결이 해제되었습니다. 페이지를 새로고침하는 것을 권장합니다.');
      
      // 5. 선택적으로 페이지 새로고침 (사용자 확인 후)
      const shouldReload = window.confirm('완전한 연결 해제를 위해 페이지를 새로고침하시겠습니까?');
      if (shouldReload) {
        window.location.reload();
      }

    } catch (error) {
      console.error('❌ 지갑 연결 해제 중 오류:', error);
      alert('지갑 연결 해제 중 일부 오류가 발생했습니다. 페이지를 새로고침해주세요.');
    }
  };

  // 에어드랍 권한 확인
  const checkAirdropRights = async () => {
    if (!user?.email) {
      setAirdropRights({ hasRights: false, numTokens: 0, message: '로그인이 필요합니다.' });
      return;
    }

    setIsCheckingRights(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL || 'http://localhost:3002'}/api/cafe/points/check?email=${encodeURIComponent(user.email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`권한 확인 실패: ${response.status}`);
      }

      const data = await response.json();
      setAirdropRights({
        hasRights: data.hasRights,
        numTokens: data.numTokens,
        grantedAt: data.grantedAt,
        notes: data.notes,
        message: data.message
      });

      console.log('에어드랍 권한 확인:', data);

    } catch (error) {
      console.error('에어드랍 권한 확인 실패:', error);
      setAirdropRights({ 
        hasRights: false, 
        numTokens: 0, 
        message: '권한 확인 중 오류가 발생했습니다.' 
      });
    } finally {
      setIsCheckingRights(false);
    }
  };

  // 사용자 토큰 목록 가져오기 (개선된 버전)
  const fetchUserTokens = async () => {
    setIsLoadingTokens(true);
    console.log(`🔍 ${account} 토큰 목록 조회 시작...`);
    
    try {
      const tokens = [];

      // 1단계: WLC 잔액 조회
      console.log('1️⃣ WLC 잔액 조회...');
      const wlcBalance = await explorerAPI.getWLCBalance(account);
      tokens.push({
        name: 'WorldLand Coin',
        symbol: 'WLC',
        address: 'native',
        balance: wlcBalance.toFixed(6),
        decimals: 18,
        isNative: true,
        verified: true
      });
      console.log(`✅ WLC 잔액: ${wlcBalance.toFixed(6)}`);

      // 2단계: Explorer API를 통해 ERC-20 토큰 조회
      console.log('2️⃣ Explorer API로 ERC-20 토큰 조회...');
      try {
        const tokenBalanceResult = await explorerAPI.getAccountTokenBalances(account);
        if (tokenBalanceResult.success && tokenBalanceResult.tokens.length > 0) {
          tokens.push(...tokenBalanceResult.tokens.map(token => ({
            ...token,
            isNative: false
          })));
          console.log(`✅ Explorer API에서 ${tokenBalanceResult.tokens.length}개 토큰 발견`);
        } else {
          console.log('⚪ Explorer API에서 토큰 없음');
        }
      } catch (apiError) {
        console.warn('⚠️ Explorer API 실패:', apiError);
      }

      // 3단계: API 실패 시 인기 토큰들에 대해 개별 잔액 확인
      if (tokens.length <= 1) { // WLC만 있는 경우
        console.log('3️⃣ 인기 토큰 개별 잔액 확인...');
        await checkPopularTokens(tokens);
      }

      // 4단계: 잔액이 0인 토큰 제거 (WLC는 제외)
      const nonZeroTokens = tokens.filter(token => 
        token.isNative || parseFloat(token.balance) > 0
      );

      console.log(`🎯 최종 토큰 목록: ${nonZeroTokens.length}개`);
      setUserTokens(nonZeroTokens);

    } catch (error) {
      console.error('❌ 토큰 목록 조회 실패:', error);
      // 실패 시 최소한 WLC는 보여주기
      const wlcBalance = await explorerAPI.getWLCBalance(account);
      setUserTokens([{
        name: 'WorldLand Coin',
        symbol: 'WLC',
        address: 'native',
        balance: wlcBalance.toFixed(6),
        decimals: 18,
        isNative: true,
        verified: true
      }]);
    } finally {
      setIsLoadingTokens(false);
    }
  };

  // 인기 토큰들에 대해 개별 잔액 확인
  const checkPopularTokens = async (tokens) => {
    try {
      console.log('📋 인기 토큰 목록 가져오기...');
      const popularResult = await explorerAPI.getPopularTokens(10);
      
      let tokensToCheck = [];
      if (popularResult.success && popularResult.tokens.length > 0) {
        tokensToCheck = popularResult.tokens;
        console.log(`📋 인기 토큰 ${tokensToCheck.length}개 로드`);
      } else {
        // 인기 토큰 조회 실패 시 기본 토큰 사용
        const defaultResult = explorerAPI.getDefaultTokenList();
        tokensToCheck = defaultResult.tokens;
        console.log(`📋 기본 토큰 ${tokensToCheck.length}개 사용`);
      }

      console.log(`🔍 ${tokensToCheck.length}개 토큰 개별 잔액 확인...`);
      
      for (const token of tokensToCheck.slice(0, 8)) { // 최대 8개까지만
        try {
          const balance = await explorerAPI.getTokenBalance(
            account, 
            token.address, 
            token.decimals
          );
          
          if (balance > 0) {
            console.log(`💰 ${token.symbol}: ${balance.toFixed(6)} 발견`);
            tokens.push({
              ...token,
              balance: balance.toFixed(6),
              isNative: false
            });
          } else {
            console.log(`⚪ ${token.symbol}: 잔액 0`);
          }
        } catch (tokenError) {
          console.warn(`⚠️ ${token.symbol} 개별 조회 실패:`, tokenError);
        }
      }
    } catch (error) {
      console.error('❌ 인기 토큰 확인 실패:', error);
    }
  };

  // 컴포넌트 마운트 시 권한 확인 및 토큰 목록 로드
  useEffect(() => {
    if (user?.email) {
      checkAirdropRights();
    }
    if (account) {
      fetchUserTokens();
    }
  }, [account, user?.email]);

  // 토큰 선택
  const selectToken = (token) => {
    setSelectedToken(token);
    if (token.isNative) {
      setIsWLC(true);
      setTokenAddress('');
    } else {
      setIsWLC(false);
      setTokenAddress(token.address);
    }
  };

  // 새 수신자 추가
  const addRecipient = () => {
    setRecipients([...recipients, { address: '', amount: '' }]);
  };

  // 수신자 삭제
  const removeRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  // 수신자 정보 업데이트
  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  // Excel 파일 업로드
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const xlsxLib = await loadXLSX();
    if (!xlsxLib) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = xlsxLib.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = xlsxLib.utils.sheet_to_json(worksheet, { header: 1 });

        console.log('Excel 원본 데이터:', jsonData);

        if (jsonData.length < 2) {
          alert('Excel 파일에 데이터가 없습니다.');
          return;
        }

        // 헤더 행 (첫 번째 행)
        const headers = jsonData[0];
        console.log('헤더:', headers);

        // 주소와 수량 컬럼 인덱스 찾기
        let addressIndex = -1;
        let amountIndex = -1;

        // A, B 컬럼 순서로 찾기 (컬럼명 무관)
        if (headers.length >= 2) {
          addressIndex = 0; // A 컬럼
          amountIndex = 1;  // B 컬럼
        } else {
          // 컬럼명으로 찾기 (백업)
          headers.forEach((header, index) => {
            const headerStr = String(header).toLowerCase();
            if (headerStr.includes('address') || headerStr.includes('주소') || headerStr === 'a') {
              addressIndex = index;
            } else if (headerStr.includes('amount') || headerStr.includes('수량') || headerStr === 'b') {
              amountIndex = index;
            }
          });
        }

        console.log('주소 컬럼 인덱스:', addressIndex, '수량 컬럼 인덱스:', amountIndex);

        if (addressIndex === -1 || amountIndex === -1) {
          alert('주소와 수량 컬럼을 찾을 수 없습니다. A열에 주소, B열에 수량을 입력해주세요.');
          return;
        }

        // 데이터 행들 처리 (헤더 제외)
        const newRecipients = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          const address = row[addressIndex];
          const amount = row[amountIndex];

          if (address && amount) {
            newRecipients.push({
              address: String(address).trim(),
              amount: String(amount).trim()
            });
          }
        }

        console.log('처리된 수신자 목록:', newRecipients);

        if (newRecipients.length === 0) {
          alert('유효한 데이터가 없습니다. A열에 주소, B열에 수량을 입력해주세요.');
          return;
        }

        setRecipients(newRecipients);
        alert(`${newRecipients.length}개의 수신자가 추가되었습니다.`);
      } catch (error) {
        console.error('파일 읽기 오류:', error);
        alert('파일을 읽는 중 오류가 발생했습니다: ' + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
    
    // 파일 입력 초기화 (같은 파일 재선택 가능)
    event.target.value = '';
  };

  // 샘플 Excel 파일 다운로드
  const downloadSampleFile = async () => {
    const xlsxLib = await loadXLSX();
    if (!xlsxLib) return;

    // A, B 컬럼으로 데이터 생성 (헤더 없이)
    const sampleData = [
      ['Address', 'Amount'], // 헤더 행
      ['0x33A642da3349333C143Fc11118a1921CaE760211', '20'],
      ['0xeA523CFF72a3De73E9183e3D6c58717463043867', '25'],
      ['0x1234567890123456789012345678901234567890', '100'],
      ['0x2345678901234567890123456789012345678901', '150']
    ];

    const ws = xlsxLib.utils.aoa_to_sheet(sampleData);
    const wb = xlsxLib.utils.book_new();
    xlsxLib.utils.book_append_sheet(wb, ws, 'Airdrop List');
    xlsxLib.writeFile(wb, 'airdrop_sample.xlsx');
  };

  // 주소 유효성 검사
  const isValidAddress = (address) => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  // 수량 유효성 검사
  const isValidAmount = (amount) => {
    const num = parseFloat(amount);
    return !isNaN(num) && num > 0;
  };

  // 폼 유효성 검사
  const validateForm = () => {
    const newErrors = [];
    
    if (recipients.length === 0) {
      newErrors.push('최소 하나의 수신자를 추가해주세요.');
    }

    if (!isWLC && !tokenAddress) {
      newErrors.push('토큰을 선택하거나 토큰 주소를 입력해주세요.');
    }

    if (!isWLC && tokenAddress && !isValidAddress(tokenAddress)) {
      newErrors.push('유효한 토큰 주소를 입력해주세요.');
    }

    // 선택된 토큰의 잔액 확인
    if (selectedToken && !selectedToken.isNative) {
      const totalAmount = recipients.reduce((sum, recipient) => {
        return sum + (parseFloat(recipient.amount) || 0);
      }, 0);
      
      if (totalAmount > parseFloat(selectedToken.balance)) {
        newErrors.push(`보유 잔액(${selectedToken.balance} ${selectedToken.symbol})이 부족합니다. 총 필요 수량: ${totalAmount.toFixed(6)} ${selectedToken.symbol}`);
      }
    }

    recipients.forEach((recipient, index) => {
      if (!recipient.address) {
        newErrors.push(`${index + 1}번째 수신자 주소를 입력해주세요.`);
      } else if (!isValidAddress(recipient.address)) {
        newErrors.push(`${index + 1}번째 수신자 주소가 유효하지 않습니다.`);
      }

      if (!recipient.amount) {
        newErrors.push(`${index + 1}번째 수신자 수량을 입력해주세요.`);
      } else if (!isValidAmount(recipient.amount)) {
        newErrors.push(`${index + 1}번째 수신자 수량이 유효하지 않습니다.`);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // WLC 에어드랍
  const sendWLCAirdrop = async (recipient) => {
    const amountInWei = (parseFloat(recipient.amount) * Math.pow(10, 18)).toString(16);
    
    const provider = window.selectedEthereumProvider || window.ethereum;
    
    const txParams = {
      from: account,
      to: recipient.address,
      value: '0x' + amountInWei,
      gas: '0x5208',
    };

    return await provider.request({
      method: 'eth_sendTransaction',
      params: [txParams],
    });
  };

  // 토큰 에어드랍 (ERC-20)
  const sendTokenAirdrop = async (recipient) => {
    const amountInWei = (parseFloat(recipient.amount) * Math.pow(10, 18)).toString(16);
    
    const provider = window.selectedEthereumProvider || window.ethereum;
    
    const transferFunction = '0xa9059cbb';
    const paddedAddress = recipient.address.substring(2).padStart(64, '0');
    const paddedAmount = amountInWei.padStart(64, '0');
    const data = transferFunction + paddedAddress + paddedAmount;

    const txParams = {
      from: account,
      to: tokenAddress,
      data: data,
      gas: '0x186a0',
    };

    return await provider.request({
      method: 'eth_sendTransaction',
      params: [txParams],
    });
  };

  // 에어드랍 실행
  const executeAirdrop = async () => {
    if (!validateForm()) return;

    // 권한 재확인
    if (!airdropRights.hasRights || airdropRights.numTokens <= 0) {
      alert('에어드랍 권한이 없습니다. 관리자에게 문의하세요.');
      return;
    }

    // 권한 사용 확인
    const confirmMessage = `에어드랍을 실행하면 권한이 1회 차감됩니다.\n\n현재 보유 권한: ${airdropRights.numTokens}회\n실행 후 잔여 권한: ${airdropRights.numTokens - 1}회\n\n계속하시겠습니까?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsProcessing(true);
    setProcessedCount(0);
    setSuccessCount(0);
    setTotalCount(recipients.length);
    setErrors([]);

    try {
      // 서버에 권한 사용 요청
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL || 'http://localhost:3002'}/api/cafe/points/use`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          userInfo: {
            uid: user.uid,
            email: user.email,
            nickname: userProfile?.nickname || user.displayName
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '권한 확인 실패');
      }

      const rightsResult = await response.json();
      if (!rightsResult.success) {
        throw new Error(rightsResult.error || '권한 사용 실패');
      }

      console.log('✅ 에어드랍 권한 사용 승인:', rightsResult);

      // 권한 상태 업데이트
      setAirdropRights(prev => ({
        ...prev,
        numTokens: rightsResult.remainingTokens,
        hasRights: rightsResult.remainingTokens > 0
      }));

    } catch (error) {
      console.error('❌ 에어드랍 권한 사용 실패:', error);
      setIsProcessing(false);
      alert(`권한 확인 실패: ${error.message}`);
      return;
    }

    const newErrors = [];
    let successfulTransfers = 0;

    console.log(`🚀 에어드랍 시작: ${recipients.length}건 처리 예정`);

    for (let i = 0; i < recipients.length; i++) {
      const recipient = recipients[i];
      const currentIndex = i + 1;
      
      console.log(`📤 ${currentIndex}/${recipients.length} 처리 중: ${recipient.address}`);
      
      try {
        let txHash;
        
        if (isWLC || (selectedToken && selectedToken.isNative)) {
          console.log(`💰 WLC 전송: ${recipient.amount} WLC → ${recipient.address}`);
          txHash = await sendWLCAirdrop(recipient);
        } else {
          console.log(`🪙 토큰 전송: ${recipient.amount} ${selectedToken?.symbol || 'TOKEN'} → ${recipient.address}`);
          txHash = await sendTokenAirdrop(recipient);
        }
        
        successfulTransfers++;
        setSuccessCount(successfulTransfers);
        
        console.log(`✅ ${currentIndex}/${recipients.length} 성공: ${txHash}`);
        
        // 성공한 거래에 대한 시각적 피드백
        updateRecipientStatus(i, 'success', txHash);
        
      } catch (error) {
        console.error(`❌ ${currentIndex}/${recipients.length} 실패 (${recipient.address}):`, error);
        
        let errorMessage = '전송 실패';
        
        // 사용자가 거부한 경우
        if (error.code === 4001) {
          errorMessage = '사용자가 거부함';
          console.log('🛑 사용자가 거래를 거부했습니다. 에어드랍을 중단합니다.');
          newErrors.push(`${recipient.address}: 사용자가 거래를 거부하여 에어드랍이 중단되었습니다.`);
          break; // 사용자가 거부하면 전체 중단
        } else if (error.code === -32603) {
          errorMessage = '내부 오류';
        } else if (error.code === -32000) {
          errorMessage = '잔액 부족 또는 가스 부족';
        } else {
          errorMessage = error.message || '알 수 없는 오류';
        }
        
        newErrors.push(`${recipient.address}: ${errorMessage}`);
        updateRecipientStatus(i, 'failed', null, errorMessage);
      }
      
      setProcessedCount(currentIndex);
      
      // 마지막 거래가 아니면 딜레이 추가 (네트워크 과부하 방지)
      if (i < recipients.length - 1) {
        console.log('⏳ 다음 거래까지 1초 대기...');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    setErrors(newErrors);
    setIsProcessing(false);
    
    const failedCount = newErrors.length;
    console.log(`🎯 에어드랍 완료: 성공 ${successfulTransfers}건, 실패 ${failedCount}건`);
    
    // 완료 메시지
    if (failedCount === 0) {
      alert(`🎉 에어드랍 완료!\n모든 ${successfulTransfers}건이 성공적으로 전송되었습니다.\n\n잔여 에어드랍 권한: ${airdropRights.numTokens}회`);
    } else if (successfulTransfers === 0) {
      alert(`😞 에어드랍 실패!\n모든 ${failedCount}건이 실패했습니다.\n\n잔여 에어드랍 권한: ${airdropRights.numTokens}회`);
    } else {
      alert(`⚡ 에어드랍 완료!\n성공: ${successfulTransfers}건\n실패: ${failedCount}건\n\n잔여 에어드랍 권한: ${airdropRights.numTokens}회\n\n실패한 거래는 오류 목록에서 확인하세요.`);
    }
  };

  // 수신자 상태 업데이트 (실시간 피드백용)
  const updateRecipientStatus = (index, status, txHash = null, errorMsg = null) => {
    setRecipients(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        status: status, // 'success', 'failed', 'processing'
        txHash: txHash,
        errorMessage: errorMsg
      };
      return updated;
    });
  };

  // 모든 입력 필드 초기화
  const clearAll = () => {
    setRecipients([]);
    setTokenAddress('');
    setIsWLC(true);
    setSelectedToken(null);
    setErrors([]);
    setProcessedCount(0);
    setTotalCount(0);
    setSuccessCount(0);
  };

  return (
    <div className="token-airdrop">
      {/* Header */}
      <div className="airdrop-header">
        <button onClick={onBack} className="back-btn">
          <ArrowLeft className="icon" />
          뒤로가기
        </button>
        <h1>Token Airdrop</h1>
        {isConnected && (
          <button onClick={disconnectWallet} className="disconnect-wallet-btn">
            🔓 연결 해제
          </button>
        )}
      </div>

      {/* 권한 확인 섹션 */}
      <div className="airdrop-rights-section">
        {isCheckingRights ? (
          <div className="rights-loading">
            <div className="spinner"></div>
            <span>에어드랍 권한을 확인하는 중...</span>
          </div>
        ) : (
          <div className={`rights-info ${airdropRights.hasRights ? 'has-rights' : 'no-rights'}`}>
            <div className="rights-header">
              <span className="rights-icon">
                {airdropRights.hasRights ? '✅' : '❌'}
              </span>
              <div className="rights-text">
                <span className="rights-status">
                  {airdropRights.hasRights ? '에어드랍 권한 보유' : '에어드랍 권한 없음'}
                </span>
                <span className="rights-detail">
                  {airdropRights.hasRights 
                    ? `${airdropRights.numTokens}회 사용 가능` 
                    : '관리자에게 권한을 요청하세요'
                  }
                </span>
              </div>
              <button 
                onClick={checkAirdropRights} 
                className="refresh-rights-btn"
                disabled={isCheckingRights}
              >
                <RefreshCw className={`icon ${isCheckingRights ? 'spinning' : ''}`} />
              </button>
            </div>
            {airdropRights.message && (
              <div className="rights-message">
                {airdropRights.message}
              </div>
            )}
            {!airdropRights.hasRights && (
              <div className="rights-contact">
                <p>에어드랍 권한이 필요한 경우 관리자에게 문의하세요.</p>
                <span className="contact-email">worldlandcafe@gmail.com</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 권한이 없으면 나머지 UI 비활성화 */}
      {!airdropRights.hasRights ? (
        <div className="no-access-overlay">
          <div className="no-access-message">
            <AlertCircle className="icon" />
            <h3>에어드랍 권한이 필요합니다</h3>
            <p>이 기능을 사용하려면 관리자로부터 에어드랍 권한을 받아야 합니다.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Token Selection Section */}
          <div className="token-selection-section">
            <div className="section-header">
              <h3>에어드랍할 토큰 선택</h3>
              <button 
                onClick={fetchUserTokens}
                disabled={isLoadingTokens}
                className="refresh-btn"
                title="토큰 목록 새로고침"
              >
                <RefreshCw className={`icon ${isLoadingTokens ? 'spinning' : ''}`} />
                {isLoadingTokens ? '로딩 중...' : '새로고침'}
              </button>
            </div>
            
            {isLoadingTokens ? (
              <div className="loading-tokens">
                <div className="spinner"></div>
                <span>WorldLand Explorer에서 토큰 목록을 불러오는 중...</span>
              </div>
            ) : (
              <div className="token-list">
                {userTokens.length === 0 ? (
                  <div className="no-tokens">
                    <p>보유한 토큰이 없습니다.</p>
                  </div>
                ) : (
                  userTokens.map((token, index) => (
                    <div 
                      key={index}
                      className={`token-item ${selectedToken?.address === token.address ? 'selected' : ''}`}
                      onClick={() => selectToken(token)}
                    >
                      <div className="token-info">
                        <div className="token-name">
                          <span className="token-symbol">{token.symbol}</span>
                          <span className="token-full-name">{token.name}</span>
                        </div>
                        <div className="token-balance">
                          <span className="balance-amount">{token.balance}</span>
                          <span className="balance-symbol">{token.symbol}</span>
                        </div>
                      </div>
                      {!token.isNative && (
                        <div className="token-address">
                          <span className="address-label">주소:</span>
                          <span className="address-value" title={token.address}>
                            {token.address.substring(0, 6)}...{token.address.substring(token.address.length - 4)}
                          </span>
                          {token.verified && (
                            <span className="verified-badge">✓</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* 커스텀 토큰 주소 입력 */}
            <div className="custom-token-section">
              <h4>또는 토큰 주소 직접 입력</h4>
              <div className="custom-token-input">
                <input
                  type="text"
                  placeholder="토큰 컨트랙트 주소 (0x...)"
                  value={tokenAddress}
                  onChange={(e) => {
                    setTokenAddress(e.target.value);
                    setSelectedToken(null);
                    setIsWLC(false);
                  }}
                  className="address-input"
                />
              </div>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="upload-section">
            <h3>수신자 목록 추가</h3>
            <div className="upload-options">
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="upload-btn"
              >
                <Upload className="icon" />
                Excel 파일 업로드
              </button>
              <button 
                onClick={downloadSampleFile}
                className="sample-btn"
              >
                <Download className="icon" />
                샘플 파일 다운로드
              </button>
              <button 
                onClick={addRecipient}
                className="add-btn"
              >
                <Plus className="icon" />
                수동 추가
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <div className="upload-info">
              <FileText className="icon" />
              <span>Excel 파일의 A열에 주소, B열에 수량을 입력해주세요. (헤더 행은 자동으로 무시됩니다)</span>
            </div>
          </div>

          {/* Recipients List */}
          {recipients.length > 0 && (
            <div className="recipients-section">
              <div className="recipients-header">
                <h3>수신자 목록 ({recipients.length}명)</h3>
                <button onClick={clearAll} className="clear-all-btn">
                  모두 삭제
                </button>
              </div>
              
              <div className="recipients-list">
                {recipients.map((recipient, index) => (
                  <div key={index} className={`recipient-item ${recipient.status || ''}`}>
                    <div className="recipient-number">
                      {index + 1}
                      {isProcessing && (
                        <div className="recipient-status">
                          {index < processedCount && recipient.status === 'success' && (
                            <CheckCircle className="status-icon success" />
                          )}
                          {index < processedCount && recipient.status === 'failed' && (
                            <AlertCircle className="status-icon failed" />
                          )}
                          {index === processedCount && (
                            <div className="status-icon processing">
                              <div className="spinner-small"></div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="recipient-inputs">
                      <input
                        type="text"
                        placeholder="수신자 주소 (0x...)"
                        value={recipient.address}
                        onChange={(e) => updateRecipient(index, 'address', e.target.value)}
                        className="address-input"
                        disabled={isProcessing}
                      />
                      <input
                        type="number"
                        placeholder="수량"
                        value={recipient.amount}
                        onChange={(e) => updateRecipient(index, 'amount', e.target.value)}
                        className="amount-input"
                        step="0.000001"
                        min="0"
                        disabled={isProcessing}
                      />
                    </div>
                    
                    {/* 실시간 상태 표시 */}
                    <div className="recipient-status-info">
                      {recipient.status === 'success' && recipient.txHash && (
                        <div className="tx-success">
                          <span className="tx-label">성공</span>
                          <a 
                            href={`https://scan.worldland.foundation/tx/${recipient.txHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tx-link"
                            title="Explorer에서 보기"
                          >
                            📄 TX
                          </a>
                        </div>
                      )}
                      {recipient.status === 'failed' && (
                        <div className="tx-failed">
                          <span className="tx-label">실패</span>
                          <span className="error-msg" title={recipient.errorMessage}>
                            {recipient.errorMessage && recipient.errorMessage.length > 20 
                              ? recipient.errorMessage.substring(0, 20) + '...'
                              : recipient.errorMessage || '오류'}
                          </span>
                        </div>
                      )}
                      {index === processedCount && isProcessing && (
                        <div className="tx-processing">
                          <span className="tx-label">전송 중...</span>
                        </div>
                      )}
                    </div>

                    <button 
                      onClick={() => removeRecipient(index)}
                      className="remove-btn"
                      disabled={isProcessing}
                    >
                      <Trash2 className="icon" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="errors-section">
              <div className="error-header">
                <AlertCircle className="icon" />
                <span>오류 목록</span>
              </div>
              <ul className="error-list">
                {errors.map((error, index) => (
                  <li key={index} className="error-item">{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Progress Section */}
          {isProcessing && (
            <div className="progress-section">
              <div className="progress-header">
                <span>에어드랍 진행 중... ({processedCount}/{totalCount})</span>
                <span className="progress-percentage">
                  {Math.round((processedCount / totalCount) * 100)}%
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${(processedCount / totalCount) * 100}%` }}
                ></div>
              </div>
              <div className="progress-stats">
                <span className="success-count">✅ 성공: {successCount}</span>
                <span className="fail-count">❌ 실패: {processedCount - successCount}</span>
                <span className="pending-count">⏳ 대기: {totalCount - processedCount}</span>
              </div>
              
              {/* 현재 처리 중인 항목 표시 */}
              {processedCount < totalCount && (
                <div className="current-processing">
                  <div className="processing-indicator">
                    <div className="spinner"></div>
                    <span>
                      {processedCount + 1}번째 처리 중: {recipients[processedCount]?.address?.substring(0, 10)}...
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Execute Button */}
          <div className="execute-section">
            <div className="execute-summary">
              <p>총 {recipients.length}명에게 {selectedToken ? selectedToken.symbol : (isWLC ? 'WLC' : '토큰')}을 에어드랍합니다.</p>
              {selectedToken && (
                <div className="selected-token-info">
                  <span>선택된 토큰: {selectedToken.name} ({selectedToken.symbol})</span>
                  <span>보유 잔액: {selectedToken.balance} {selectedToken.symbol}</span>
                </div>
              )}
              <div className="rights-warning">
                ⚡ 에어드랍 실행 시 권한이 1회 차감됩니다. (현재 보유: {airdropRights.numTokens}회)
              </div>
              <p className="gas-warning">
                ⚠️ 각 전송마다 가스비가 발생합니다. 충분한 WLC 잔액을 확인해주세요.
              </p>
            </div>
            <button 
              onClick={executeAirdrop}
              disabled={isProcessing || recipients.length === 0 || (!selectedToken && !isWLC && !tokenAddress) || !airdropRights.hasRights}
              className="execute-btn"
            >
              {isProcessing ? (
                <>
                  <div className="spinner"></div>
                  처리 중...
                </>
              ) : (
                <>
                  <Send className="icon" />
                  에어드랍 실행 (권한 1회 차감)
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TokenAirdrop;